import { useQuery } from '@tanstack/react-query'
import {
  type FormEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { threadCopy } from '@/copy'
import { Chip } from '@/ui/Chip/Chip'
import { Combobox, type ComboboxOption } from '@/ui/Combobox/Combobox'
import { Composer } from '@/ui/Composer/Composer'
import { ApiError, NetworkError } from '../../api/client'
import { resourcesFor } from '../../api/resources'
import { useThreadsQuery, useWorkspaceAgentsQuery } from '../../hooks/queries'
import { activeAgentsInTopic, useStreamStore } from '../../store/stream'
import type {
  CreateMessageBody,
  MessageAcceptanceResponse,
} from '../../types/api'
import { newClientMessageId } from '../../utils/clientMessageId'
import {
  detectActiveToken,
  formatMention,
  type MentionRef,
  type ResourceRef,
  reconcileMentionRefs,
  reconcileResourceRefs,
  replaceToken,
} from '../../utils/tokens'
import styles from './ConsoleComposer.module.css'
import { threadComposerLabel, topicComposerLabel } from './composerLabel'
import {
  type MentionAgent,
  type MentionOption,
  mentionOptions,
} from './mentionOptions'

interface Props {
  topicId: string
  topicName?: string
  workspaceId?: string
  openThreadId: string | null
  onSend: (body: CreateMessageBody) => Promise<MessageAcceptanceResponse>
  /** Abort the in-flight turn for this topic (topic-scoped cancel). */
  onCancel?: () => void
  users: string[]
  /** Shell-level hide (console-v2 resources tab) — the composer is the
   * threads-tab surface; keep it mounted to preserve drafts. */
  hidden?: boolean
}

interface SubmissionState {
  body: CreateMessageBody
  status: 'rejected' | 'unknown'
  message?: string
}

interface QueuedTask {
  body: CreateMessageBody
  rawInput: string
  refs: ResourceRef[]
  mentions: MentionRef[]
  /** Value of draftEditSeqRef when the task was queued. */
  editSeq: number
}

function isUnknownSend(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof ApiError && error.status >= 500 && !error.isStructured)
  )
}

export function ConsoleComposer({
  topicId,
  topicName,
  workspaceId,
  openThreadId,
  onSend,
  onCancel,
  users,
  hidden = false,
}: Props) {
  const [input, setInput] = useState('')
  const [pendingRefs, setPendingRefs] = useState<ResourceRef[]>([])
  const [pendingMentions, setPendingMentions] = useState<MentionRef[]>([])
  const [submission, setSubmission] = useState<SubmissionState | null>(null)
  const [caret, setCaret] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingCaret = useRef<number | null>(null)

  const draftRef = useRef(input)
  const pendingRefsRef = useRef(pendingRefs)
  const pendingMentionsRef = useRef(pendingMentions)
  const onSendRef = useRef(onSend)

  useEffect(() => {
    draftRef.current = input
    pendingRefsRef.current = pendingRefs
    pendingMentionsRef.current = pendingMentions
    onSendRef.current = onSend
  }, [input, pendingRefs, pendingMentions, onSend])

  const queueRef = useRef<QueuedTask[]>([])
  const isProcessingRef = useRef(false)

  // Monotonic counter of user draft edits. Captured when a task is queued so
  // the success path can tell an untouched draft (e.g. one restored after a
  // failed send) from one the user has re-typed since submitting.
  const draftEditSeqRef = useRef(0)

  // Any turn in flight anywhere in this topic (main channel or a thread).
  const running = useStreamStore(
    (state) => activeAgentsInTopic(state, topicId).length > 0,
  )

  const { data: threadData } = useThreadsQuery(topicId)
  const openThread = threadData?.threads.find(
    (thread) => thread.thread_id === openThreadId,
  )

  const workspaceAgentsQuery = useWorkspaceAgentsQuery(workspaceId, {
    staleTime: 30_000,
  })
  const mentionAgents: MentionAgent[] = (workspaceAgentsQuery.data ?? [])
    .filter((agent) => agent.status === 'active')
    .map((agent) => ({ id: agent.agent_id, name: agent.name }))

  // Resources for the `#` autocomplete (topic + workspace scopes).
  const topicResourcesQuery = useQuery({
    queryKey: ['resources', 'topic', topicId],
    queryFn: () => resourcesFor({ kind: 'topic', id: topicId }).list(),
    staleTime: 10_000,
  })
  const workspaceResourcesQuery = useQuery({
    queryKey: ['resources', 'workspace', workspaceId],
    queryFn: () =>
      resourcesFor({ kind: 'workspace', id: workspaceId as string }).list(),
    enabled: !!workspaceId,
    staleTime: 10_000,
  })
  const topicResources = topicResourcesQuery.data
  const workspaceResources = workspaceResourcesQuery.data
  const resourceCandidates: ResourceRef[] = [
    ...(topicResources?.resources ?? []),
    ...(workspaceResources?.resources ?? []),
  ].map((resource) => ({
    id: resource.id,
    name: resource.name,
    kind: resource.kind,
  }))

  const frozen = submission?.status === 'unknown'

  function updateInput(value: string) {
    draftEditSeqRef.current += 1
    draftRef.current = value
    setInput(value)
  }

  async function processQueue() {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    while (queueRef.current.length > 0) {
      const task = queueRef.current[0]
      try {
        await onSendRef.current(task.body)
        // Clear only if the user hasn't edited the draft since this task was
        // queued. Without the editSeq guard, re-typing the exact sent text
        // while the request is in flight would wipe the new draft here.
        if (
          task.editSeq === draftEditSeqRef.current &&
          (draftRef.current === task.rawInput ||
            draftRef.current === task.body.content)
        ) {
          setInput('')
          setPendingRefs([])
          setPendingMentions([])
        }
        setSubmission(null)
        queueRef.current.shift()
      } catch (error) {
        queueRef.current.shift()
        const unknown = isUnknownSend(error)
        const message = unknown
          ? threadCopy.composer.unknownSend
          : error instanceof Error
            ? error.message
            : threadCopy.composer.sendFailed

        if (draftRef.current.trim() === '') {
          setInput(task.rawInput || task.body.content)
          setPendingRefs(task.refs)
          setPendingMentions(task.mentions)
        }

        setSubmission({
          body: task.body,
          status: unknown ? 'unknown' : 'rejected',
          message,
        })

        if (unknown) {
          queueRef.current = []
          break
        }
      }
    }

    isProcessingRef.current = false
  }

  function retryExact(body: CreateMessageBody) {
    const task: QueuedTask = {
      body,
      rawInput: body.content,
      refs: pendingRefsRef.current,
      mentions: pendingMentionsRef.current,
      editSeq: draftEditSeqRef.current,
    }
    queueRef.current.push(task)
    void processQueue()
  }

  async function handleSubmit(event?: FormEvent) {
    event?.preventDefault()
    if (submission?.status === 'unknown') return
    if (
      submission?.status === 'rejected' &&
      (!input.trim() || input === submission.body.content)
    ) {
      retryExact(submission.body)
      return
    }

    const content = input.trim()
    if (!content) return
    const rawInput = input
    const resourceRefs = reconcileResourceRefs(content, pendingRefs)
    // Split reconciled mentions by kind: agent ids dispatch, usernames
    // attribute (mentioned_usernames, D3).
    const reconciled = reconcileMentionRefs(content, pendingMentions)
    const mentionIds = reconciled
      .filter((m) => m.kind === 'agent')
      .map((m) => m.id)
    const mentionedUsers = reconciled
      .filter((m) => m.kind === 'user')
      .map((m) => m.id)
    const body: CreateMessageBody = {
      content,
      client_message_id: newClientMessageId(),
      mention_ids: mentionIds,
      mentioned_usernames:
        mentionedUsers.length > 0 ? mentionedUsers : undefined,
      resource_refs: resourceRefs.length > 0 ? resourceRefs : undefined,
      thread_id: openThreadId ?? undefined,
    }

    const task: QueuedTask = {
      body,
      rawInput,
      refs: pendingRefs,
      mentions: pendingMentions,
      editSeq: draftEditSeqRef.current,
    }

    setInput('')
    setPendingRefs([])
    setPendingMentions([])
    setSubmission(null)

    queueRef.current.push(task)
    void processQueue()
  }

  const selectedMentionIds = new Set(
    reconcileMentionRefs(input, pendingMentions).map((m) => m.id),
  )
  const selectedMentions = pendingMentions.filter(
    (mention, index, all) =>
      selectedMentionIds.has(mention.id) &&
      all.findIndex((candidate) => candidate.id === mention.id) === index &&
      input.includes(formatMention(mention.name)),
  )

  const active = useMemo(() => detectActiveToken(input, caret), [input, caret])
  const options: MentionOption[] = active
    ? mentionOptions(active, mentionAgents, users, resourceCandidates)
    : []

  // The source behind the active trigger: `@` the agents, `#` the resources.
  let pending = false
  let failed = false
  if (active?.trigger === '@') {
    pending = !!workspaceId && workspaceAgentsQuery.isPending
    failed = workspaceAgentsQuery.isError
  } else if (active?.trigger === '#') {
    pending =
      topicResourcesQuery.isPending ||
      (!!workspaceId && workspaceResourcesQuery.isPending)
    failed = topicResourcesQuery.isError || workspaceResourcesQuery.isError
  }
  const suggestionsError = failed
    ? threadCopy.composer.suggestionsFailed
    : undefined
  // Open only with something to show: options, or their loading or failure.
  // An unmatched token (`thanks @bob`, a `#` query running to line end) stays
  // closed, so Enter sends the literal text.
  const open =
    !!active &&
    (options.length > 0 || pending || failed) &&
    !dismissed &&
    !frozen

  function retrySuggestions() {
    if (active?.trigger === '@') {
      void workspaceAgentsQuery.refetch()
    } else if (active?.trigger === '#') {
      if (topicResourcesQuery.isError) void topicResourcesQuery.refetch()
      if (workspaceResourcesQuery.isError) {
        void workspaceResourcesQuery.refetch()
      }
    }
  }

  function syncCaret() {
    const position = textareaRef.current?.selectionStart
    if (position !== undefined) setCaret(position)
  }

  function handleValue(value: string) {
    updateInput(value)
    setDismissed(false)
    setCaret(textareaRef.current?.selectionStart ?? value.length)
  }

  function pick(option: ComboboxOption) {
    const picked = options.find((candidate) => candidate.id === option.id)
    if (!active || !picked) return
    const next = replaceToken(input, active.start, caret, picked.insert)
    updateInput(next.text)
    pendingCaret.current = next.caret
    const { resource, mention } = picked
    if (resource) {
      setPendingRefs((current) => {
        const refs = [...current, resource]
        pendingRefsRef.current = refs
        return refs
      })
    }
    if (mention) {
      setPendingMentions((current) => {
        const mentions = [...current, mention]
        pendingMentionsRef.current = mentions
        return mentions
      })
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: a pick's new text is the trigger; the caret waits for it to render
  useLayoutEffect(() => {
    const position = pendingCaret.current
    if (position === null) return
    pendingCaret.current = null
    textareaRef.current?.setSelectionRange(position, position)
    setCaret(position)
  }, [input])

  const label = openThreadId
    ? threadComposerLabel(openThread?.title, openThread?.root_preview)
    : topicComposerLabel(topicName)

  return (
    <div className={styles.composer} hidden={hidden}>
      <Combobox
        label={label}
        listLabel={threadCopy.composer.suggestions}
        value={input}
        onValue={handleValue}
        options={options}
        onSelect={pick}
        emptyMessage={threadCopy.composer.noMatches}
        open={open}
        onOpenChange={(next) => {
          if (!next) setDismissed(true)
        }}
        pending={pending}
        error={suggestionsError}
        onRetry={retrySuggestions}
        ref={textareaRef}
        renderField={({ ref, onKeyDown, onSelect, onClick, ...field }) => (
          <Composer
            value={input}
            onValue={handleValue}
            onSend={() => void handleSubmit()}
            label={label}
            openThreadId={openThreadId}
            ref={ref}
            fieldProps={{
              ...field,
              // Base UI's key handling runs only while the list is open, and
              // never for Home/End: it moves the caret to the ends of the whole
              // value and opens the list on the arrows. An IME's Enter belongs
              // to the composition, as in Composer.
              onKeyDown: (e) => {
                if (!open || e.key === 'Home' || e.key === 'End') return
                const composing = e.nativeEvent.isComposing || e.keyCode === 229
                if (e.key === 'Enter' && composing) return
                onKeyDown?.(e)
                // Nothing highlighted (no auto-select, D139): Enter is the
                // person's literal text. Close the list and let Composer send.
                if (e.key === 'Enter' && !e.shiftKey && !e.defaultPrevented) {
                  setDismissed(true)
                }
              },
              onSelect: (e) => {
                onSelect?.(e)
                syncCaret()
              },
              onClick: (e) => {
                onClick?.(e)
                syncCaret()
              },
            }}
            mentions={
              selectedMentions.length > 0 ? (
                <ul
                  aria-label={threadCopy.composer.selectedMentions}
                  className={styles.selected}
                >
                  {selectedMentions.map((mention) => (
                    <li key={mention.id}>
                      <Chip
                        variant="kind"
                        label={threadCopy.composer.mention(mention.name)}
                      />
                    </li>
                  ))}
                </ul>
              ) : undefined
            }
            frozen={frozen ? threadCopy.composer.frozenUnknown : undefined}
            error={submission?.message}
            onRetry={submission ? () => retryExact(submission.body) : undefined}
            onStop={running && onCancel ? onCancel : undefined}
          />
        )}
      />
    </div>
  )
}
